/**
 * ADMIN MODULE — Email Reference.
 * Internal ops layer — platform_admins only (RequireSuperAdmin, registered in
 * index.js). Never import this from a product module.
 *
 * Every email Brand Command can send, in one place, so nobody has to read four
 * edge functions and two tables to answer "what do we send, to whom, and what
 * does it look like".
 *
 * ⚠️ THE POINT OF THIS SCREEN IS THAT IT IS NOT A DOCUMENT.
 * A hardcoded list would drift within a week — this repo has been bitten by
 * exactly that four times (backlog 29, 60, 64, 74: ARCHITECTURE files describing
 * modules as they were at port time). So the *structure* here is static, because
 * triggers and recipients only change when someone changes the code, but every
 * fact that moves on its own is READ LIVE:
 *   - the sender addresses and the master switch  → email_settings
 *   - whether the shared template is branded      → email_template_settings
 *   - which nurture steps are live                → nurture_templates
 *   - what has actually been sent and delivered   → email_log
 * If you add an email, add it to FAMILIES below in the same commit.
 *
 * Layout: this is a reference, so it is built to be SCANNED, not operated. One
 * card per family, each with its own accent colour; one small card per email
 * with a definition list, because "Sent when" and "Goes to" are prose. The two
 * standing warnings (Auth never reaches email_log; "sent" ≠ delivered) are
 * callouts on purpose — they were body text once and nobody read them.
 */

import { useEffect, useState } from "react";
import { supabase } from "../../../shared/utils/supabaseClient.js";
import AdminLayout from "../components/AdminLayout.jsx";
import "./EmailReferenceView.css";

/* ── The static half: what we send, when, and to whom ─────────────────────── */

const FAMILIES = [
  {
    key: "transactional",
    name: "Transactional",
    builtBy: "send-email → _shared/emailTemplate.ts",
    coBranded: true,
    logged: true,
    note:
      "Co-branded as the TENANT, powered by Brand Command — these reach the " +
      "customer's distributors, who have no relationship with us. Header uses " +
      "the tenant's own colour and logo; the footer says who it is from.",
    emails: [
      {
        subject: "Order POS-#### received",
        kind: "pos_order_received",
        trigger: "A distributor submits the POS order form embedded on the customer's own website",
        to: "Whoever submitted the order",
        source: "pos-order-public",
      },
      {
        subject: "Your POS order POS-#### has shipped",
        kind: "pos_shipped",
        trigger: "A POS order moves INTO Shipped (the transition, not every save)",
        to: "Ship-to contact (only if “Notify distributor” is ticked) + the submitter",
        source: "pos-shipped-notify",
      },
      {
        subject: "Your product sample request has shipped",
        kind: "sample_shipped",
        trigger: "A sample request moves INTO Shipped",
        to: "Contact + submitter",
        source: "pos-shipped-notify",
      },
      {
        subject: "Test email",
        kind: "(none)",
        trigger: "Admin → Email → Send test",
        to: "Whatever address you type",
        source: "send-email (staff JWT path)",
        notCoBranded: true,
      },
    ],
  },
  {
    key: "support",
    name: "Support tickets",
    builtBy: "ticket-notify → send-email",
    coBranded: false,
    logged: true,
    note:
      "Brand Command branding on purpose — these genuinely are from us. Routed " +
      "through send-email since backlog 89; before that they bypassed email_log " +
      "entirely and were invisible here.",
    emails: [
      {
        subject: "[BC-####] <ticket subject>",
        kind: "ticket_new",
        trigger: "A customer opens a support ticket",
        to: "All Brand Command staff",
        source: "ticket-notify",
      },
      {
        subject: "[BC-####] <ticket subject>",
        kind: "ticket_reply",
        trigger: "Either side replies",
        to: "The other side — staff if the customer replied, the submitter if staff did",
        source: "ticket-notify",
      },
    ],
  },
  {
    key: "auth",
    name: "Supabase Auth",
    builtBy: "GoTrue templates over Brevo SMTP",
    coBranded: false,
    logged: false,
    // Load-bearing: this is the one thing about Auth mail people get wrong, so
    // it renders as a callout instead of the third sentence of a grey note.
    warning: {
      title: "These NEVER reach email_log",
      body:
        "GoTrue sends them directly, so nothing on this page — and nothing " +
        "anywhere in the app — records whether they were delivered. If a " +
        "customer says they never got a confirmation, this screen cannot " +
        "answer it.",
    },
    note:
      "Templates are generated from supabase/auth-templates/, not pasted into " +
      "the dashboard, because project auth config is outside the repo and " +
      "outside every deploy.",
    emails: [
      { subject: "Confirm your email address", trigger: "Signup — the first email a customer ever receives", to: "The new user" },
      { subject: "Your sign-in link", trigger: "Magic-link login", to: "The user" },
      { subject: "Reset your password", trigger: "Forgot password", to: "The user" },
      { subject: "Confirm your new email address", trigger: "Email change", to: "The new address" },
      { subject: "You've been invited", trigger: "Workspace invitation", to: "The invitee" },
    ],
  },
];

/* ── UI bits ──────────────────────────────────────────────────────────────── */

const Pill = ({ tone, children }) => (
  <span className={`emref-pill tone-${tone}`}>{children}</span>
);

function Callout({ tone, title, children }) {
  return (
    <div className={`emref-callout tone-${tone}`} role="note">
      <span className="emref-callout-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>
      <div className="emref-callout-text">
        {title && <strong className="emref-callout-title">{title}</strong>}
        {children}
      </div>
    </div>
  );
}

/** One tile in the live-configuration strip. `tone` drives the colour bar +
 *  dot, so a bad state is visible without reading the value. */
function Stat({ label, value, sub, tone, swatch }) {
  return (
    <div className={`emref-stat${tone ? ` is-${tone}` : ""}`}>
      <span className="emref-stat-k">{label}</span>
      <span className="emref-stat-v">
        {tone && <span className="emref-dot" />}
        {swatch && <span className="emref-swatch" style={{ background: swatch }} />}
        {value}
      </span>
      {sub && <span className="emref-stat-sub">{sub}</span>}
    </div>
  );
}

/* Only paint a swatch for something that is actually a colour — the value comes
   from a DB row, and a junk string would just render an invisible box. */
const isHex = (v) => typeof v === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v.trim());

function shortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function Counts({ stat }) {
  if (!stat) return <span className="emref-quiet">Never sent</span>;
  const last = shortDate(stat.last);
  return (
    <>
      <span className="emref-count"><b>{stat.sent}</b> sent</span>
      {stat.delivered > 0 && <span className="emref-count is-ok"><b>{stat.delivered}</b> delivered</span>}
      {stat.failed > 0 && <span className="emref-count is-bad"><b>{stat.failed}</b> failed</span>}
      {/* Rows that are neither `sent` nor `failed` — nothing writes one today,
          so this is normally absent. Shown as its own neutral count rather than
          folded into "failed", which is what item 119 was about. */}
      {stat.other > 0 && (
        <span className="emref-count" title="Rows whose status is neither sent nor failed">
          <b>{stat.other}</b> other
        </span>
      )}
      {last && <span className="emref-when">last {last}</span>}
    </>
  );
}

export default function EmailReferenceView() {
  const [live, setLive] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [settings, template, nurture, log] = await Promise.all([
          supabase.from("email_settings")
            .select("enabled, from_name, from_email, reply_to, nurture_from_name, nurture_from_email")
            .eq("provider", "brevo").limit(1).maybeSingle(),
          supabase.from("email_template_settings")
            .select("header_logo_url, header_bg_color, footer_text")
            .eq("email_type", "internal").limit(1).maybeSingle(),
          supabase.from("nurture_templates")
            .select("sequence_key, step, subject, is_active, interval_days")
            .order("sequence_key").order("step"),
          // Per-kind rollup. Small table; the alternative is a view, and this
          // screen is staff-only and rarely opened.
          supabase.from("email_log")
            .select("kind, status, delivered_at, created_at")
            .order("created_at", { ascending: false })
            .limit(500),
        ]);
        const rows = log.data ?? [];
        const byKind = {};
        for (const r of rows) {
          const k = r.kind || "(none)";
          byKind[k] ??= { sent: 0, failed: 0, other: 0, delivered: 0, last: null };
          // Enumerate rather than treating "not sent" as "failed" (item 119).
          // `send-email` only writes `sent` / `failed` today, but this screen is
          // what staff check when a customer says an email never arrived — so a
          // queued/retrying row, or any status added later, must not be reported
          // as a failure that never happened. Unknown statuses get their own
          // bucket and are shown separately.
          if (r.status === "sent") byKind[k].sent++;
          else if (r.status === "failed") byKind[k].failed++;
          else byKind[k].other++;
          if (r.delivered_at) byKind[k].delivered++;
          if (!byKind[k].last) byKind[k].last = r.created_at;
        }
        setLive({
          settings: settings.data ?? null,
          template: template.data ?? null,
          nurture: nurture.data ?? [],
          byKind,
          logCount: rows.length,
        });
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  // `loaded` is the fetch having COMPLETED, which is not the same as a row
  // existing — see the Stat tiles below (item 118).
  const loaded = !!live;
  const s = live?.settings;
  const t = live?.template;
  const branded = !!t?.header_logo_url;

  return (
    <AdminLayout
      title="Email Reference"
      subtitle="Every email the platform can send, who receives it, and how it is branded — staff only."
    >
      <div className="emref">
        <p className="emref-lede">
          The <strong>structure</strong> below is maintained by hand — triggers
          and recipients only change when someone changes the code. Everything
          that moves on its own is <strong>read live</strong>: sender settings,
          template branding, nurture steps, and delivery counts.
        </p>

        {err && (
          <Callout tone="danger" title="Could not read the live settings">
            <p>{err}</p>
          </Callout>
        )}

        {/* ── Live configuration — the "is anything wrong right now" panel ── */}
        <section className="emref-status">
          <div className="emref-status-head">
            <span className="emref-overline">Live configuration</span>
            <a href="/admin/email">Change in Email Settings →</a>
          </div>
          <div className="emref-status-grid">
            <Stat
              label="Sending"
              value={s ? (s.enabled ? "Enabled" : "Disabled") : "…"}
              tone={s ? (s.enabled ? "good" : "bad") : null}
            />
            {/* `…` means STILL LOADING and nothing else. Both of these reads are
                `.maybeSingle()`, which returns null — not an error — when the row
                does not exist, so a missing row used to sit on the ellipsis
                forever and read as "still fetching" (item 118). `loaded` tracks
                completion separately from row-presence, so an absent row now says
                so, in the same red tone the panel already uses for "No logo set". */}
            <Stat
              label="Transactional sender"
              value={!loaded ? "…" : (s ? s.from_name : "Not configured")}
              sub={s ? s.from_email : null}
              tone={loaded && !s ? "bad" : null}
            />
            <Stat
              label="Replies go to"
              value={!loaded ? "…" : (s ? (s.reply_to || "— (per-send only)") : "Not configured")}
              tone={loaded && !s ? "bad" : null}
            />
            <Stat
              label="Nurture sender"
              value={!loaded ? "…" : (s ? s.nurture_from_name : "Not configured")}
              sub={s ? s.nurture_from_email : null}
              tone={loaded && !s ? "bad" : null}
            />
            <Stat
              label="Shared template"
              value={!loaded ? "…" : (t ? (branded ? "Branded (logo set)" : "No logo set") : "Not configured")}
              tone={!loaded ? null : (t ? (branded ? "good" : "bad") : "bad")}
            />
            <Stat
              label="Header colour"
              value={!loaded ? "…" : (t?.header_bg_color || "—")}
              swatch={isHex(t?.header_bg_color) ? t.header_bg_color : null}
              tone={loaded && !t ? "bad" : null}
            />
          </div>
          <p className="emref-status-foot">
            Two senders on purpose: <code className="emref-code">noreply@</code>{" "}
            for automated product mail, <code className="emref-code">info@</code>{" "}
            for nurture, which should look like it came from a person.
          </p>
        </section>

        {/* Load-bearing: every count on the page is an acceptance, not a receipt. */}
        <Callout tone="warn" title="“Sent” means accepted, not delivered">
          <p>
            Every count on this page is the provider <em>accepting</em> the
            message — not it arriving. The delivered figure comes from the
            provider's webhook and can lag by several minutes. Counts cover the
            last <b>{live ? live.logCount : "…"}</b> logged sends.
          </p>
        </Callout>

        {/* ── The families ─────────────────────────────────────────────── */}
        {FAMILIES.map((f) => (
          <section key={f.key} className={`emref-fam fam-${f.key}`}>
            <header className="emref-fam-head">
              <h3>{f.name}</h3>
              <Pill tone="accent">{f.coBranded ? "Co-branded as tenant" : "Brand Command"}</Pill>
              {f.logged
                ? <Pill tone="good">In email_log</Pill>
                : <Pill tone="danger">Never in email_log</Pill>}
              <code className="emref-code emref-fam-built">{f.builtBy}</code>
            </header>

            <div className="emref-fam-body">
              {f.warning && (
                <Callout tone="danger" title={f.warning.title}>
                  <p>{f.warning.body}</p>
                </Callout>
              )}
              <p className="emref-fam-note">{f.note}</p>

              <div className="emref-mails">
                {f.emails.map((e, i) => {
                  const stat = e.kind && live ? live.byKind[e.kind] : null;
                  return (
                    <article key={i} className="emref-mail">
                      <div className="emref-mail-head">
                        <span className="emref-mail-subject">{e.subject}</span>
                        {e.notCoBranded && f.coBranded && (
                          <Pill tone="warn">Not co-branded — from us</Pill>
                        )}
                        {e.kind && <code className="emref-code">{e.kind}</code>}
                        {e.source && <span className="emref-src"><i>via </i>{e.source}</span>}
                      </div>
                      <dl className="emref-dl">
                        <dt>When</dt><dd>{e.trigger}</dd>
                        <dt>To</dt><dd>{e.to}</dd>
                      </dl>
                      {/* Unlogged families get no footer at all — the family
                          pill and callout already say why, and repeating "not
                          tracked" on every card is just noise. */}
                      {f.logged && (
                        <div className="emref-mail-foot"><Counts stat={stat} /></div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        ))}

        {/* ── Nurture, read live ───────────────────────────────────────── */}
        <section className="emref-fam fam-nurture">
          <header className="emref-fam-head">
            <h3>Nurture / marketing</h3>
            <Pill tone="accent">Brand Command</Pill>
            <Pill tone="good">In email_log</Pill>
            <code className="emref-code emref-fam-built">_shared/nurture + nurture_templates</code>
          </header>
          <div className="emref-fam-body">
            <p className="emref-fam-note">
              Read live from the database — steps, timing and whether each one is
              switched on. A step that is not active sends nothing; subscribers
              wait rather than skipping it. Edit in{" "}
              <a href="/admin/nurture">Email Nurture</a>.
            </p>
            <div className="emref-steps">
              {(live?.nurture ?? []).map((n) => (
                <div
                  key={`${n.sequence_key}-${n.step}`}
                  className={`emref-step${n.is_active ? "" : " is-paused"}`}
                >
                  <span className="emref-step-n">{n.step}</span>
                  <span className="emref-step-main">
                    <span className="emref-step-subject">{n.subject}</span>
                    <span className="emref-step-meta">
                      <code className="emref-code">{n.sequence_key}</code>
                      <span>
                        {n.step === 0
                          ? "on signup"
                          : n.interval_days ? `+${n.interval_days} days` : "—"}
                      </span>
                    </span>
                  </span>
                  {n.is_active ? <Pill tone="good">Active</Pill> : <Pill tone="warn">Paused</Pill>}
                </div>
              ))}
              {live && live.nurture.length === 0 && (
                <div className="emref-empty">No nurture templates.</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
